import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { generateCertificateId } from '../utils';
import { downloadCertificate } from './certificate';

const STAGE_INTRO = 'intro';
const STAGE_QUIZ = 'quiz';
const STAGE_RESULT = 'result';

function normalizeOptions(question) {
  if (question?.question_type === 'true_false') {
    return [
      { id: 'true', text: 'True' },
      { id: 'false', text: 'False' },
    ];
  }
  const raw = question?.options;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((o, idx) => {
      if (o && typeof o === 'object') {
        return {
          id: String(o.id ?? o.key ?? o.value ?? idx),
          text: String(o.text ?? o.label ?? o.value ?? ''),
        };
      }
      return { id: String(idx), text: String(o) };
    });
  }
  if (typeof raw === 'object') {
    return Object.entries(raw).map(([id, text]) => ({
      id: String(id),
      text: String(text),
    }));
  }
  return [];
}

function normalizeCorrect(raw) {
  if (raw === null || raw === undefined) return [];
  if (typeof raw === 'boolean') return [raw ? 'true' : 'false'];
  if (Array.isArray(raw)) return raw.map((v) => String(v).toLowerCase());
  if (typeof raw === 'object' && 'value' in raw)
    return [String(raw.value).toLowerCase()];
  return [String(raw).toLowerCase()];
}

function isCorrect(question, given) {
  const correct = normalizeCorrect(question.correct_answer);
  const givenArr = Array.isArray(given)
    ? given.map((g) => String(g).toLowerCase())
    : given !== undefined && given !== null && given !== ''
      ? [String(given).toLowerCase()]
      : [];
  if (correct.length !== givenArr.length) return false;
  const sortedC = [...correct].sort();
  const sortedG = [...givenArr].sort();
  return sortedC.every((v, i) => v === sortedG[i]);
}

export default function QuizFlow({ assignment, user, onClose, onCompleted }) {
  const module = assignment.module || {};
  const passingScore = module.passing_score ?? 80;

  const [stage, setStage] = useState(STAGE_INTRO);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('inservice_questions')
        .select('*')
        .eq('module_id', assignment.module_id)
        .order('sort_order', { ascending: true });
      if (cancelled) return;
      if (error) {
        setLoadError(error.message);
        setQuestions([]);
      } else {
        setQuestions(data || []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [assignment.module_id]);

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIdx];
  const currentOptions = useMemo(
    () => (currentQuestion ? normalizeOptions(currentQuestion) : []),
    [currentQuestion]
  );
  const isMultiSelect =
    currentQuestion && currentQuestion.question_type === 'multi_select';
  const isTrueFalse =
    currentQuestion && currentQuestion.question_type === 'true_false';

  function selectOption(optionId) {
    setAnswers((prev) => {
      const next = { ...prev };
      if (isMultiSelect) {
        const existing = Array.isArray(next[currentQuestion.id])
          ? next[currentQuestion.id]
          : [];
        next[currentQuestion.id] = existing.includes(optionId)
          ? existing.filter((x) => x !== optionId)
          : [...existing, optionId];
      } else {
        next[currentQuestion.id] = optionId;
      }
      return next;
    });
  }

  function isOptionSelected(optionId) {
    const ans = answers[currentQuestion?.id];
    if (Array.isArray(ans)) return ans.includes(optionId);
    return ans === optionId;
  }

  function canAdvance() {
    const ans = answers[currentQuestion?.id];
    if (Array.isArray(ans)) return ans.length > 0;
    return ans !== undefined && ans !== null && ans !== '';
  }

  async function submitQuiz() {
    setSubmitting(true);

    let correctCount = 0;
    const answersById = {};
    for (const q of questions) {
      const given = answers[q.id];
      answersById[q.id] = given ?? null;
      if (isCorrect(q, given)) correctCount += 1;
    }

    const score = totalQuestions
      ? Math.round((correctCount / totalQuestions) * 100)
      : 0;
    const passed = score >= passingScore;

    const { count: priorCount } = await supabase
      .from('inservice_completions')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('module_id', assignment.module_id);

    const attempt_number = (priorCount || 0) + 1;
    const certificate_id = passed ? generateCertificateId() : null;

    let expires_at = null;
    if (passed && module.expiration_days) {
      const exp = new Date();
      exp.setDate(exp.getDate() + Number(module.expiration_days));
      expires_at = exp.toISOString();
    }

    const completionPayload = {
      user_id: user.id,
      module_id: assignment.module_id,
      assignment_id: assignment.id,
      score,
      passed,
      answers: answersById,
      question_count: totalQuestions,
      passing_score: passingScore,
      certificate_id,
      expires_at,
      attempt_number,
      completed_at: new Date().toISOString(),
    };

    const { error: insertError } = await supabase
      .from('inservice_completions')
      .insert(completionPayload);

    if (insertError) {
      setSubmitting(false);
      setResult({
        error: insertError.message,
        score,
        passed,
        correctCount,
      });
      setStage(STAGE_RESULT);
      return;
    }

    if (passed) {
      await supabase
        .from('inservice_assignments')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
        })
        .eq('id', assignment.id);
    }

    setSubmitting(false);
    setResult({
      score,
      passed,
      correctCount,
      certificate_id,
      expires_at,
    });
    setStage(STAGE_RESULT);

    if (passed && onCompleted) {
      onCompleted();
    }
  }

  function retake() {
    setAnswers({});
    setCurrentIdx(0);
    setResult(null);
    setStage(STAGE_INTRO);
  }

  if (loading) {
    return (
      <div className="quiz-overlay">
        <div className="quiz-container">
          <div className="loading-state">Loading module…</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="quiz-overlay">
        <div className="quiz-container">
          <div className="quiz-header">
            <button className="btn-ghost" onClick={onClose}>
              ← Back
            </button>
          </div>
          <div className="empty-state">
            <h3>Unable to load module</h3>
            <p>{loadError}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-overlay">
      <div className="quiz-container">
        <div className="quiz-header">
          <button className="btn-ghost" onClick={onClose}>
            ← Back to dashboard
          </button>
          <span style={{ fontSize: 14, color: 'var(--gray-500)' }}>
            {module.category}
          </span>
        </div>

        {stage === STAGE_INTRO && (
          <IntroStage
            module={module}
            totalQuestions={totalQuestions}
            onBegin={() => setStage(STAGE_QUIZ)}
          />
        )}

        {stage === STAGE_QUIZ && currentQuestion && (
          <QuestionStage
            question={currentQuestion}
            options={currentOptions}
            isMultiSelect={isMultiSelect}
            isTrueFalse={isTrueFalse}
            isOptionSelected={isOptionSelected}
            onSelect={selectOption}
            currentIdx={currentIdx}
            total={totalQuestions}
            onPrev={
              currentIdx > 0 ? () => setCurrentIdx((i) => i - 1) : null
            }
            onNext={
              currentIdx < totalQuestions - 1
                ? () => setCurrentIdx((i) => i + 1)
                : null
            }
            onSubmit={
              currentIdx === totalQuestions - 1 ? submitQuiz : null
            }
            canAdvance={canAdvance()}
            submitting={submitting}
          />
        )}

        {stage === STAGE_QUIZ && !currentQuestion && (
          <div className="quiz-card">
            <h3>This module has no questions yet.</h3>
            <button className="btn btn-outline" onClick={onClose}>
              Back to dashboard
            </button>
          </div>
        )}

        {stage === STAGE_RESULT && result && (
          <ResultStage
            result={result}
            module={module}
            user={user}
            questions={questions}
            answers={answers}
            passingScore={passingScore}
            onRetake={retake}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function IntroStage({ module, totalQuestions, onBegin }) {
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const contentRef = useRef(null);

  const checkScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const reachedEnd =
      el.scrollHeight - el.scrollTop - el.clientHeight <= 8;
    if (reachedEnd) setScrolledToEnd(true);
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 8) {
      setScrolledToEnd(true);
    }
  }, [module.content, module.description]);

  const html =
    module.content || module.description || '<p>No module content available.</p>';

  return (
    <div className="quiz-card">
      <h2 style={{ marginBottom: 6 }}>{module.title}</h2>
      <p style={{ color: 'var(--gray-500)', marginBottom: 18 }}>
        {totalQuestions} question{totalQuestions === 1 ? '' : 's'} ·{' '}
        {module.passing_score ?? 80}% to pass
        {module.estimated_minutes
          ? ` · ~${module.estimated_minutes} min`
          : ''}
      </p>
      <div
        className="module-content"
        ref={contentRef}
        onScroll={checkScroll}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {!scrolledToEnd && (
        <p className="scroll-hint">
          Scroll to the end of the content to enable the quiz.
        </p>
      )}
      <button
        type="button"
        className="btn btn-primary btn-lg"
        onClick={onBegin}
        disabled={totalQuestions === 0 || !scrolledToEnd}
      >
        Begin Quiz
      </button>
    </div>
  );
}

function QuestionStage({
  question,
  options,
  isMultiSelect,
  isTrueFalse,
  isOptionSelected,
  onSelect,
  currentIdx,
  total,
  onPrev,
  onNext,
  onSubmit,
  canAdvance,
  submitting,
}) {
  const pct = total ? Math.round(((currentIdx + 1) / total) * 100) : 0;
  return (
    <div className="quiz-card">
      <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--gray-500)' }}>
        Question {currentIdx + 1} of {total}
        {isMultiSelect && ' · select all that apply'}
      </div>
      <div className="progress-bar">
        <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="question-text">{question.question_text}</div>

      {isTrueFalse ? (
        <div className="tf-row">
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`tf-button ${isOptionSelected(opt.id) ? 'selected' : ''}`}
              onClick={() => onSelect(opt.id)}
              disabled={submitting}
            >
              {opt.text}
            </button>
          ))}
        </div>
      ) : (
        options.map((opt) => {
          const selected = isOptionSelected(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              className={`option ${selected ? 'selected' : ''}`}
              onClick={() => onSelect(opt.id)}
              disabled={submitting}
            >
              <span
                className={`option-mark ${isMultiSelect ? 'checkbox' : ''}`}
                aria-hidden="true"
              >
                {selected ? (isMultiSelect ? '✓' : '●') : ''}
              </span>
              <span>{opt.text}</span>
            </button>
          );
        })
      )}

      <div className="quiz-footer">
        <button
          type="button"
          className="btn btn-outline"
          onClick={onPrev}
          disabled={!onPrev || submitting}
          style={{ visibility: onPrev ? 'visible' : 'hidden' }}
        >
          Previous
        </button>
        {onSubmit ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={!canAdvance || submitting}
          >
            {submitting ? 'Submitting…' : 'Submit Quiz'}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={onNext}
            disabled={!canAdvance || submitting}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
}

function ResultStage({
  result,
  module,
  user,
  questions,
  answers,
  passingScore,
  onRetake,
  onClose,
}) {
  const { score, passed, correctCount, certificate_id, error } = result;

  function handleDownload() {
    downloadCertificate({
      userName: user.name || user.email,
      moduleTitle: module.title,
      certificateId: certificate_id,
      completedAt: new Date().toISOString(),
      score,
    });
  }

  return (
    <div className="quiz-card">
      {error && (
        <div
          style={{
            background: '#FDECEA',
            color: 'var(--red)',
            padding: 12,
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          Could not record completion: {error}
        </div>
      )}

      <div className={`score-icon ${passed ? 'score-pass' : 'score-fail'}`}>
        {passed ? '✓' : '✗'}
      </div>
      <div className={`score-percent ${passed ? 'score-pass' : 'score-fail'}`}>
        {score}%
      </div>
      <p className="score-detail">
        {correctCount} of {questions.length} correct · {passingScore}% required
      </p>

      {passed ? (
        <>
          <h3 style={{ textAlign: 'center', marginBottom: 8 }}>
            Congratulations — module complete
          </h3>
          <p style={{ textAlign: 'center', color: 'var(--gray-500)' }}>
            {module.title}
          </p>
          {certificate_id && (
            <div className="cert-id">Certificate ID: {certificate_id}</div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            {certificate_id && (
              <button className="btn btn-gold" onClick={handleDownload}>
                Download Certificate
              </button>
            )}
            <button className="btn btn-outline" onClick={onClose}>
              Back to dashboard
            </button>
          </div>
        </>
      ) : (
        <>
          <h3
            style={{
              textAlign: 'center',
              marginBottom: 8,
              color: 'var(--red)',
            }}
          >
            Did not pass
          </h3>
          <p
            style={{
              textAlign: 'center',
              color: 'var(--gray-500)',
              marginBottom: 24,
            }}
          >
            Review the explanations below and retake the quiz.
          </p>

          <div style={{ marginBottom: 24 }}>
            {questions.map((q, idx) => {
              const given = answers[q.id];
              const ok = isCorrect(q, given);
              const opts = normalizeOptions(q);
              const correctIds = normalizeCorrect(q.correct_answer);
              return (
                <div key={q.id} className="review-block">
                  <div className="q">
                    {idx + 1}. {q.question_text}
                  </div>
                  {opts.map((o) => {
                    const isC = correctIds.includes(
                      String(o.id).toLowerCase()
                    );
                    const wasGiven = Array.isArray(given)
                      ? given.includes(o.id)
                      : given === o.id;
                    let cls = 'option';
                    if (isC) cls += ' correct';
                    else if (wasGiven) cls += ' incorrect';
                    return (
                      <div key={o.id} className={cls}>
                        <span>{o.text}</span>
                        {isC && (
                          <span style={{ marginLeft: 'auto' }}>✓</span>
                        )}
                        {!isC && wasGiven && (
                          <span style={{ marginLeft: 'auto' }}>✗</span>
                        )}
                      </div>
                    );
                  })}
                  {q.explanation && (
                    <div className="explanation">
                      <strong>{ok ? 'Note: ' : 'Explanation: '}</strong>
                      {q.explanation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            style={{ display: 'flex', justifyContent: 'center', gap: 12 }}
          >
            <button className="btn btn-outline" onClick={onClose}>
              Back to dashboard
            </button>
            <button className="btn btn-primary" onClick={onRetake}>
              Retake Quiz
            </button>
          </div>
        </>
      )}
    </div>
  );
}
